/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumb from "../../components/Breadcrumb";
import { PatientAuthContext } from "../../contexts/PatientAuthContext";
import { patientApi } from "../../utils/patientApi";

const PatientProfileMain = () => {
  const navigate = useNavigate();
  const { patient, isLoading, logout, refreshPatient } = useContext(PatientAuthContext);

  const [profileForm, setProfileForm] = useState({ name: "", email: "" });
  const [pwdForm, setPwdForm] = useState({ currentPassword: "", password: "", confirmPassword: "" });
  const [saving, setSaving] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);

  useEffect(() => {
    if (!isLoading && !patient) {
      navigate("/patient-login");
    }
    if (patient) {
      setProfileForm({ name: patient.name || "", email: patient.email || "" });
    }
  }, [patient, isLoading, navigate]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await patientApi.updateProfile({ name: profileForm.name, email: profileForm.email });
      await refreshPatient();
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(err.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (pwdForm.password !== pwdForm.confirmPassword) {
      return toast.error("Passwords do not match");
    }
    if (pwdForm.password.length < 6) {
      return toast.error("Password must be at least 6 characters");
    }
    setChangingPwd(true);
    try {
      await patientApi.setPassword({
        password: pwdForm.password,
        ...(patient.hasPassword ? { currentPassword: pwdForm.currentPassword } : {}),
      });
      toast.success("Password set successfully! You can now login with MRNO & password.");
      setPwdForm({ currentPassword: "", password: "", confirmPassword: "" });
    } catch (err) {
      toast.error(err.message || "Failed to set password");
    } finally {
      setChangingPwd(false);
    }
  };

  if (isLoading) {
    return (
      <main>
        <Breadcrumb title="My Profile" />
        <div className="container pt-120 pb-120 text-center">Loading…</div>
      </main>
    );
  }

  if (!patient) return null;

  return (
    <main>
      <Breadcrumb title="My Profile" />

      <div className="it-signup-area pt-80 pb-120">
        <div className="container">
          <div className="row gy-4">
            {/* ── Info Card ─────────────────────────────────────────────── */}
            <div className="col-xl-4 col-lg-5">
              <div style={{ background: "#f9f9f9", borderRadius: 12, padding: "30px 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                <div className="text-center mb-25">
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "#e8f0fe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                      fontSize: 32,
                      color: "#3b7df8",
                    }}
                  >
                    <i className="fa-regular fa-user"></i>
                  </div>
                  <h5 style={{ fontWeight: 700, marginBottom: 4 }}>
                    {patient.firstName} {patient.lastName}
                  </h5>
                  {patient.name && patient.name !== `${patient.firstName} ${patient.lastName}`.trim() && (
                    <p style={{ color: "#777", fontSize: "0.9rem", marginBottom: 4 }}>{patient.name}</p>
                  )}
                  <span
                    style={{
                      display: "inline-block",
                      background: "#3b7df820",
                      color: "#3b7df8",
                      padding: "2px 12px",
                      borderRadius: 20,
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      letterSpacing: 1,
                    }}
                  >
                    {patient.mrno}
                  </span>
                </div>
                <hr />
                <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: "0.9rem", color: "#555" }}>
                  {patient.nic && (
                    <li className="mb-10">
                      <i className="fa-regular fa-id-card me-2" style={{ color: "#aaa" }}></i>
                      <strong>NIC:</strong> {patient.nic}
                    </li>
                  )}
                  {patient.fatherName && (
                    <li className="mb-10">
                      <i className="fa-regular fa-people-group me-2" style={{ color: "#aaa" }}></i>
                      <strong>Father:</strong> {patient.fatherName}
                    </li>
                  )}
                  {patient.dateOfBirth && (
                    <li className="mb-10">
                      <i className="fa-regular fa-calendar me-2" style={{ color: "#aaa" }}></i>
                      <strong>DOB:</strong> {new Date(patient.dateOfBirth).toLocaleDateString()}
                    </li>
                  )}
                  {patient.phone && (
                    <li className="mb-10">
                      <i className="fa-regular fa-phone me-2" style={{ color: "#aaa" }}></i>
                      {patient.phone}
                    </li>
                  )}
                  {patient.email && (
                    <li className="mb-10">
                      <i className="fa-regular fa-envelope me-2" style={{ color: "#aaa" }}></i>
                      {patient.email}
                    </li>
                  )}
                </ul>
                <hr />
                <button
                  onClick={logout}
                  style={{
                    width: "100%",
                    background: "#fff",
                    border: "1px solid #e74c3c",
                    color: "#e74c3c",
                    borderRadius: 8,
                    padding: "10px 0",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  <i className="fa-regular fa-right-from-bracket me-2"></i>
                  Logout
                </button>
              </div>
            </div>

            {/* ── Forms ─────────────────────────────────────────────────── */}
            <div className="col-xl-8 col-lg-7">
              {/* Profile update form */}
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  padding: "30px 28px",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  marginBottom: 24,
                }}
              >
                <h5 style={{ fontWeight: 700, marginBottom: 20 }}>Update Profile</h5>
                <form onSubmit={handleProfileSave}>
                  <div className="row gy-3">
                    <div className="col-md-6">
                      <label style={{ fontSize: "0.85rem", fontWeight: 500, marginBottom: 6, display: "block" }}>Display Name</label>
                      <div className="it-signup-input">
                        <input
                          type="text"
                          placeholder="Display name"
                          value={profileForm.name}
                          onChange={(e) => setProfileForm((f) => ({ ...f, name: e.target.value }))}
                          disabled={saving}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label style={{ fontSize: "0.85rem", fontWeight: 500, marginBottom: 6, display: "block" }}>Email</label>
                      <div className="it-signup-input">
                        <input
                          type="email"
                          placeholder="Email address"
                          value={profileForm.email}
                          onChange={(e) => setProfileForm((f) => ({ ...f, email: e.target.value }))}
                          disabled={saving}
                        />
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="it-btn-theme mt-20" disabled={saving} style={{ padding: "10px 32px", borderRadius: 8 }}>
                    {saving ? "Saving…" : "Save Changes"}
                  </button>
                </form>
              </div>

              {/* Password form */}
              <div style={{ background: "#fff", borderRadius: 12, padding: "30px 28px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                <h5 style={{ fontWeight: 700, marginBottom: 8 }}>{patient.hasPassword ? "Change Password" : "Set Password"}</h5>
                <p style={{ color: "#777", fontSize: "0.85rem", marginBottom: 20 }}>
                  {patient.hasPassword
                    ? "Change your login password. You can also login using OTP."
                    : "Set a password to enable quick MRNO + password login without OTP."}
                </p>
                <form onSubmit={handlePasswordSave}>
                  <div className="row gy-3">
                    {patient.hasPassword && (
                      <div className="col-12">
                        <label style={{ fontSize: "0.85rem", fontWeight: 500, marginBottom: 6, display: "block" }}>Current Password</label>
                        <div className="it-signup-input">
                          <input
                            type="password"
                            placeholder="Current password"
                            value={pwdForm.currentPassword}
                            onChange={(e) => setPwdForm((f) => ({ ...f, currentPassword: e.target.value }))}
                            disabled={changingPwd}
                          />
                        </div>
                      </div>
                    )}
                    <div className="col-md-6">
                      <label style={{ fontSize: "0.85rem", fontWeight: 500, marginBottom: 6, display: "block" }}>New Password</label>
                      <div className="it-signup-input">
                        <input
                          type="password"
                          placeholder="New password (min 6 chars)"
                          value={pwdForm.password}
                          onChange={(e) => setPwdForm((f) => ({ ...f, password: e.target.value }))}
                          disabled={changingPwd}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label style={{ fontSize: "0.85rem", fontWeight: 500, marginBottom: 6, display: "block" }}>Confirm Password</label>
                      <div className="it-signup-input">
                        <input
                          type="password"
                          placeholder="Confirm password"
                          value={pwdForm.confirmPassword}
                          onChange={(e) => setPwdForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                          disabled={changingPwd}
                        />
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="it-btn-theme mt-20" disabled={changingPwd} style={{ padding: "10px 32px", borderRadius: 8 }}>
                    {changingPwd ? "Saving…" : patient.hasPassword ? "Change Password" : "Set Password"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default PatientProfileMain;
